import React, { useContext, useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';
import { assign } from 'xstate';
import { IntlShape } from 'react-intl';

import { BaseComponent } from '../Component';
import { SendContext } from '../../SendContext';
import { MachineContext } from '../../MachineContext';
import { translateWithFallback } from '../../util';

import { ValueCard, ValueCardProps } from './ValueCard';

export interface HotOrNotSelectionComponent extends BaseComponent {
  type: 'hotOrNotSelection';
  props: HotOrNotSelectionComponentProps;
}

// HotOrNotSelectionItem replaced by new interfave ValueCardProps from
// ValueCard.tsx - refactored there.

export interface HotOrNotSelectionComponentProps {
  cards: ValueCardProps[];
  saveResultTo: string;
}

/**
 * returns the list of cards that have a liked === undefined,
 * i.e., they weren't liked or disliked to far
 * @param {ValueCardProps[]} cards
 * @returns {ValueCardProps[]} array of cards
 */
function getUnseenCards(cards: ValueCardProps[]) {
  return cards.filter((value) => value.liked === undefined);
}

/**
 * get all liked cards
 * @param {HotOrNotSelectionItem[]} cards
 * @returns {Array} array of cards, including type and value props for handling in other components
 */
const getLikedCards = (cards: ValueCardProps[]) => {
  return cards.filter((card: ValueCardProps) => card.liked);
};

/**
 * get all cards that are not liked.
 * @param {HotOrNotSelectionItem[]} cards
 * @returns {Array} array of cards, including type and value props for handling in other components
 */
const getNotLikedCards = (cards: ValueCardProps[]) => {
  return cards.filter((card: ValueCardProps) => !card.liked);
};

/**
 * Get the index of the next value to process (card.liked===undefined)
 * @param {ValueCardProps} cards list of cards
 * @returns {number} index of next card or -1 of all cards are already processed
 */
const getIndexOfNextValue = (
  cards: ValueCardProps[],
  currentIndex: number
): number => {
  // calc next index
  let nextIndex = -1;
  // count number of cards to process (num objects with .liked ===undefined)
  for (
    let i = currentIndex >= cards.length - 1 ? 0 : currentIndex;
    i < cards.length;
    i++
  ) {
    if (nextIndex < 0 && cards[i].liked === undefined) {
      nextIndex = i;
    }
  }
  return nextIndex;
};

export const HotOrNotSelection: React.FC<HotOrNotSelectionComponentProps> = ({
  cards,
  saveResultTo,
}) => {
  const { send } = useContext(SendContext);

  // cardList presents only cards that were neither liked or disliked (but are undefined)
  const [cardList, setCardList] = useState<ValueCardProps[]>(
    getUnseenCards(cards)
  );

  // current index in card list
  const [currentCardIndex, setCurrentCardIndex] = useState<number>(
    getIndexOfNextValue(cardList, 0)
  );

  // normally, cards do not change dynamically in chatbot. But they may change in
  // builder, especially if we switch module language or change skip button texts.
  // In order to catch thse cases and dynamically switch languages, we have to add
  // this useEffect here.(PROD-1250)
  useEffect(() => {
    setCardList(cards);
  }, [cards]);

  const cardMax = cardList ? cardList.length : 0;

  /**
   * Check whether the nextIndex would be the end of the cardslist. If yes,
   * check whether we have to copy with some skipped cards. If there are
   * no skipped cards any more and we are at the end of the cards list,
   * automatically send the context and proceed with chat
   * @param nextIndex the next index
   * @returns
   */
  const checkSend = (
    nextIndex: number,
    localList: ValueCardProps[]
  ): boolean => {
    if (nextIndex >= cardMax || nextIndex === -1) {
      send({
        type: 'writeToContext',
        payload: {
          [saveResultTo]: getLikedCards(localList),
        },
      });
      // PROD-1402
      send({
        type: 'writeToContext',
        payload: {
          [saveResultTo + '_NOTSELECTED']: getNotLikedCards(localList),
        },
      });
      send({
        type: 'next',
      });
      return true;
    }
    // we're not a the the end of the list or there is a skipped value
    return false;
  };

  // skip a card and increase index
  const clickSkipHandler = (
    currentIndex: number,
    locallist: ValueCardProps[]
  ) => {
    // leave card untouched and increase index

    let nextIndex = getIndexOfNextValue(locallist, currentIndex);
    // count number of skippable objects in list
    const numSkippable = locallist.reduce((num, card) => {
      return num + (card.liked === undefined ? 1 : 0);
    }, 0);

    // if there the currently skipped element is the last one, stay there,
    // proceed otherweise
    if (numSkippable > 1) {
      nextIndex++;
    }
    if (!checkSend(nextIndex, locallist)) {
      setCurrentCardIndex(nextIndex);
    }
  };

  // like a card => set liked
  const clickLikeDislikeHandler = (
    likedprop: boolean,
    currentIndex: number,
    currentList: ValueCardProps[]
  ) => {
    // set liked = true and update cardList
    let newValues = cloneDeep(currentList);
    newValues[currentIndex].liked = likedprop;
    setCardList(newValues);

    // get next index to process
    const nextIndex = getIndexOfNextValue(newValues, currentIndex);
    if (!checkSend(nextIndex, newValues)) {
      setCurrentCardIndex(nextIndex);
    }
  };

  // do not display a card if index is too large ==> auto send in previous useEffect function
  if (currentCardIndex >= cardMax) {
    return <></>;
  }

  // render next card
  return (
    <ValueCard
      cardNumber={currentCardIndex + 1}
      cardAmount={cardMax}
      {...cardList[currentCardIndex]}
      skipButtonText={
        currentCardIndex < cardMax
          ? cardList[currentCardIndex].skipButtonText
          : undefined
      }
      onClickSkip={() => clickSkipHandler(currentCardIndex, cardList)}
      onClickLike={() =>
        clickLikeDislikeHandler(true, currentCardIndex, cardList)
      }
      onClickDislike={() =>
        clickLikeDislikeHandler(false, currentCardIndex, cardList)
      }
    />
  );
};

//
// render function for pushing the component on the widgte stack of xState
//
export const renderHotOrNotSelectionAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    const getValueFrom = payload.getValueFrom;

    // translate values if they exist
    const translateCard = (card: ValueCardProps) => {
      return {
        ...card,
        value: translateWithFallback(intl, card.value),
        description: card.description
          ? translateWithFallback(intl, card.description)
          : undefined,
        skipButtonText: card.skipButtonText
          ? translateWithFallback(intl, card.skipButtonText)
          : translateWithFallback(intl, payload.buttonText),
      } as ValueCardProps;
    };

    // allow only one parameter that has to contain cards and nothing else
    const cards =
      getValueFrom &&
      context.userData[getValueFrom] &&
      Array.isArray(context.userData[getValueFrom])
        ? context.userData[getValueFrom]
            .map((card: any) => {
              if (card.type === 'hotornotcard') {
                // only allow hotornotcards
                // remove .type and .value for cards
                return translateCard(card);
              } else {
                // do not allow any other types
                return undefined;
              }
            })
            .filter((card: any) => card)
        : payload.cards.map((card: any) => translateCard(card));

    newContext.widgetData.push({
      type: 'hotOrNotSelection',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        cards: cards,
        saveResultTo: payload.saveResultTo,
      },
    });
    return newContext;
  });
};
